import DroppableContext, {
  DraggableItemDeltas,
} from './context/DroppableContext';
import { useMemo, useState } from 'react';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';

interface DroppableProps {
  children: React.ReactNode | React.ReactNode[];
}

/**
 * Wraps child components in a container that allows them to be draggable.
 * Provides a context for the draggable items to share their positions relative to their initial position.

 */
const Droppable = ({ children }: DroppableProps) => {
  const [draggableItemPositions, setDraggableItemPositions] =
    useState<DraggableItemDeltas>({});

  /**
   * Handles the drag end event and updates the positions of draggable items.
   *
   * @param {DragEndEvent} event - The drag end event object.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    setDraggableItemPositions((prevPositions) => {
      const { id } = event.active;
      const { x, y } = event.delta;

      if (!prevPositions[id]) {
        return {
          ...prevPositions,
          [id]: {
            x,
            y,
          },
        };
      }

      return {
        ...prevPositions,
        [id]: {
          x: prevPositions[id].x + x,
          y: prevPositions[id].y + y,
        },
      };
    });
  };

  const memoizedContextValue = useMemo(() => {
    return { positions: draggableItemPositions };
  }, [draggableItemPositions]);

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
      autoScroll={false}
    >
      <DroppableContext.Provider value={memoizedContextValue}>
        <div className='position-relative h-100'>{children}</div>
      </DroppableContext.Provider>
    </DndContext>
  );
};

export default Droppable;
